import { getDocs, getDoc, query, where, type DocumentReference, type DocumentData } from 'firebase/firestore';
import { reviewsCol, cyclesCol, cycleDoc, userDoc, leaveRequestsCol, policiesCol, policyAcksCol, sendContributionRequestsCol, sendConfigDoc, sendRegisterCol, sendReviewsCol, sendPassportsCol } from './firestore';
import { getLineManagerGaps } from './functions';
import { formatDate } from './sendDates';
import { inAckAudience } from './policyAudience';
import { formatLeaveDate as leaveDate } from './leave';
import type { Review, Cycle, AppUser, LeaveRequest, Policy, PolicyAck, ModuleId } from '../types';


/**
 * Read documents by id, tolerating ones that are no longer there.
 *
 * A deleted document does NOT come back as "not found" for most of our
 * collections: rules like `isSchoolMember(resource.data.schoolId)` have no
 * resource to test once the document is gone, so the read is DENIED and the
 * promise rejects. Inside a Promise.all that rejects the whole batch, and the
 * To Do list's only error handler renders an empty list — so one deleted cycle
 * told every one of that school's staff they were all caught up. Silence is the
 * worst possible failure for the page whose entire job is saying what needs
 * doing, so a read that fails is dropped on its own and the rest still count.
 */
async function readDocsTolerantly<T>(
  refs: DocumentReference<DocumentData>[],
): Promise<Map<string, T & { id: string }>> {
  const out = new Map<string, T & { id: string }>();
  const snaps = await Promise.all(
    refs.map((r) => getDoc(r).catch(() => null)),
  );
  snaps.forEach((s) => {
    if (s?.exists()) out.set(s.id, { ...s.data(), id: s.id } as T & { id: string });
  });
  return out;
}

export type Priority = 'high' | 'medium' | 'low';

export interface TodoItem {
  id: string;
  type: string;
  title: string;
  subtitle: string;
  href: string;
  priority: Priority;
  dueDate?: string | null;
}

export const PRIORITY_ORDER: Record<Priority, number> = { high: 0, medium: 1, low: 2 };
export const PRIORITY_LABEL: Record<Priority, string> = { high: 'Urgent', medium: 'Due soon', low: 'Upcoming' };
export const TODO_ICONS: Record<string, string> = {
  'self-review':       '📝',
  'returned-review':   '↩️',
  'mid-year-staff':    '📋',
  'confirm-review':    '✅',
  'mid-year-lm':       '💬',
  'launch-cycle':      '🚀',
  'open-mid-year':     '📅',
  'admin-awaiting-lm': '⏳',
  'admin-returned':    '↩️',
  'admin-overdue':     '⚠️',
  'leave-approval':    '🗓️',
  'send-contribution': '🧠',
  'send-review-overdue': '🧠',
  'send-la-overdue': '🧠',
  'send-passport-stale': '🧠',
  'policy-ack':        '📜',
  'assign-line-manager': '👥',
};

function fmtDate(ts: unknown): string | null {
  // Same guard as isPast below. Without it, reading .toDate off an undefined
  // deadline throws and takes down the whole To Do page — one cycle with no
  // line-manager deadline and nobody sees any of their tasks.
  if (!ts) return null;
  const d = typeof (ts as { toDate?: () => Date }).toDate === 'function' ? (ts as { toDate: () => Date }).toDate() : null;
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
}

function isPast(ts: unknown): boolean {
  if (!ts) return false;
  const d = typeof (ts as { toDate?: () => Date }).toDate === 'function' ? (ts as { toDate: () => Date }).toDate() : null;
  if (!d) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return d < today;
}

/** How long each section of the list may take. A normal load takes well under
 *  a second; this is only the backstop for a read that will never answer. */
const SECTION_DEADLINE_MS = 15_000;

export interface TodoResult {
  items: TodoItem[];
  /** A section failed or ran out of time, so the list may be missing things.
   *  Callers must never present an incomplete empty list as "all caught up". */
  incomplete: boolean;
}

/**
 * Everything that needs this user's attention — their own PM self-reviews,
 * line-manager sign-offs for their team, (for school admins) cycle
 * administration, leave requests routed to them for a decision, policies
 * awaiting their acknowledgement. Sorted by priority. The single source of
 * truth shared by the To Do page, the home dashboard and the nav badge.
 *
 * staffCategory drives the policy-audience check; pass appUser.staffCategory.
 *
 * Each section loads on its own, in parallel, under its own deadline. A
 * Firestore one-off read can hang with no error at all (firebase-js-sdk
 * #10404: the SDK believes it is online while the connection has quietly
 * stopped delivering), and when this list was one chain of reads, one stuck
 * read took all of it — first as "Loading…" for ever, then as "Couldn't load".
 * Now whatever loaded is shown, and `incomplete` says what did not.
 */
export async function buildUserTodos(
  uid: string,
  schoolId: string,
  role: AppUser['role'],
  staffCategory?: AppUser['staffCategory'],
  /** The school's active modules (SchoolContext.activeModules). The SEND
   *  reads are skipped without it: their rules call hasModule('send'), which
   *  THROWS for a school with no `send` entry in its modules map, so the read
   *  is denied rather than empty — a logged failure on every load for every
   *  school not subscribed to SEND. Leave and policy reads have no such
   *  gate in their rules, so they still run and simply come back empty. */
  modules?: readonly ModuleId[],
): Promise<TodoResult> {
  const hasSend = modules ? modules.includes('send') : true;
  const sections: [string, () => Promise<TodoItem[]>][] = [];

  // ── SEND annual review contributions asked of ME ──────────────────────────
  // The listener shape the rule authorises: schoolId AND assigneeUid. The
  // request carries the pupil's name and a due date and nothing about the plan,
  // so this item is safe to show anyone the SENCo asked.
  if (hasSend) sections.push(['SEND contributions', async () => {
    const items: TodoItem[] = [];
    const reqSnap = await getDocs(
      query(sendContributionRequestsCol, where('schoolId', '==', schoolId), where('assigneeUid', '==', uid), where('status', '==', 'open')),
    );
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    const soonBy = new Date(Date.now() + 7 * 86_400_000).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    for (const d of reqSnap.docs) {
      const r = d.data();
      const late = r.dueDate < today;
      const soon = !late && r.dueDate <= soonBy;
      items.push({
        id: `send-contrib-${d.id}`,
        type: 'send-contribution',
        title: `Give your view for ${r.studentName}'s annual review`,
        // Same vocabulary as the leave item, not a raw yyyy-mm-dd.
        subtitle: `Asked by ${r.requestedByName} · due ${formatDate(r.dueDate)}`,
        href: '/send/contributions',
        priority: late || soon ? 'high' : 'medium',
        dueDate: late ? 'Overdue' : soon ? 'Within a week' : null,
      });
    }
    return items;
  }]);

  // ── SEND team: what the sweep found (compliance tier only) ────────────────
  // Membership is a list in sendConfig, not a role, so read it first; the
  // three reads below are Tier 1 and would be refused for anyone else.
  if (hasSend) sections.push(['SEND oversight', async () => {
    const items: TodoItem[] = [];
    const cfg = (await getDoc(sendConfigDoc(schoolId))).data();
    const team = [...(cfg?.sencoUids ?? []), ...(cfg?.sendTeamUids ?? []), ...(cfg?.leadershipViewerUids ?? [])];
    if (team.includes(uid)) {
      const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
      const [overdueSnap, laSnap, staleSnap] = await Promise.all([
        getDocs(query(sendRegisterCol, where('schoolId', '==', schoolId), where('overdue', '==', true))),
        getDocs(query(sendReviewsCol, where('schoolId', '==', schoolId), where('stage', '==', 'recommendationSent'), where('laResponseDueDate', '<', todayKey))),
        getDocs(query(sendPassportsCol, where('schoolId', '==', schoolId), where('stale', '==', true))),
      ]);
      for (const d of overdueSnap.docs) {
        const r = d.data();
        items.push({ id: `send-overdue-${d.id}`, type: 'send-review-overdue', title: `${r.studentName}'s annual review is overdue`, subtitle: r.reviewStage ? 'Review open — hold the meeting' : `Was due ${formatDate(r.nextReviewDue)} — open the review`, href: `/send/pupil/${r.studentId}`, priority: 'high', dueDate: 'Overdue' });
      }
      for (const d of laSnap.docs) {
        const r = d.data();
        items.push({ id: `send-la-${d.id}`, type: 'send-la-overdue', title: `Chase the LA about ${r.studentName}'s review`, subtitle: `Their response was due ${formatDate(r.laResponseDueDate)}`, href: `/send/pupil/${r.studentId}`, priority: 'high', dueDate: 'Overdue' });
      }
      for (const d of staleSnap.docs) {
        const r = d.data();
        items.push({ id: `send-stale-${d.id}`, type: 'send-passport-stale', title: `Refresh ${r.studentName}'s support summary`, subtitle: 'Older than a term, or the record changed since it was published', href: `/send/pupil/${r.studentId}`, priority: 'low', dueDate: null });
      }
    }
    return items;
  }]);

  // ── Leave requests waiting for MY decision (any role can be an approver) ──
  // If the leave module is off the collection simply has nothing routed to us.
  // pendingApproverUids holds the line manager at the first stage and every SLT
  // approver at the second, so one query covers both. It is non-empty only while
  // a request is pending, which is why there is no status clause.
  sections.push(['leave approvals', async () => {
    const items: TodoItem[] = [];
    const approvalsSnap = await getDocs(
      query(leaveRequestsCol, where('pendingApproverUids', 'array-contains', uid)),
    );
    for (const d of approvalsSnap.docs) {
      const r = d.data() as LeaveRequest;
      const soon = r.date <= new Date(Date.now() + 7 * 86_400_000)
        .toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
      const stage = r.status === 'pendingSlt' ? 'Sign off' : 'Decide';
      items.push({
        id: `leave-${d.id}`,
        type: 'leave-approval',
        title: `${stage} ${r.userName}'s leave request`,
        subtitle: `${leaveDate(r.date)} · ${r.typeLabel}${r.urgent ? ' · urgent' : ''}${r.escalated ? ' · escalated' : ''}`,
        href: '/leave/approvals',
        priority: soon || r.urgent || r.escalated ? 'high' : 'medium',
        dueDate: r.urgent ? 'Urgent' : soon ? 'Within a week' : null,
      });
    }
    return items;
  }]);

  // ── Active policies awaiting MY acknowledgement ───────────────────────────
  // Mirrors the "Needs your acknowledgement" section on the Policies page:
  // active + in my audience + my latest ack is for an older version.
  if (role !== 'superAdmin') sections.push(['policy acknowledgements', async () => {
    const items: TodoItem[] = [];
    const [policiesSnap, acksSnap] = await Promise.all([
      getDocs(query(policiesCol, where('schoolId', '==', schoolId), where('status', '==', 'active'))),
      getDocs(query(policyAcksCol, where('schoolId', '==', schoolId), where('userId', '==', uid))),
    ]);
    const ackedVersion = new Map<string, number>();
    acksSnap.docs.forEach((d) => {
      const a = d.data() as PolicyAck;
      ackedVersion.set(a.policyId, Math.max(ackedVersion.get(a.policyId) ?? 0, a.policyVersion));
    });
    for (const d of policiesSnap.docs) {
      const p = { ...(d.data() as Policy), id: d.id };
      if (!inAckAudience(p, staffCategory)) continue;
      if ((ackedVersion.get(d.id) ?? 0) >= p.version) continue;
      items.push({
        id: `policy-ack-${d.id}`,
        type: 'policy-ack',
        title: `Read and acknowledge: ${p.title}`,
        subtitle: p.version > 1 ? `Updated policy (version ${p.version})` : 'New policy',
        href: `/policies/${d.id}`,
        priority: 'medium',
        dueDate: null,
      });
    }
    return items;
  }]);

  // ── Staff / LM / headteacher own review tasks ───────────────────────────────
  if (role !== 'superAdmin' && role !== 'schoolAdmin') sections.push(['own reviews', async () => {
    const items: TodoItem[] = [];
    const myReviewsSnap = await getDocs(
      query(reviewsCol, where('schoolId', '==', schoolId), where('revieweeId', '==', uid)),
    );
    const reviewDocs = myReviewsSnap.docs.map((d) => ({ ...d.data(), id: d.id } as Review & { id: string }));
    const cycleIds = [...new Set(reviewDocs.map((r) => r.cycleId))];
    const cycleMap = await readDocsTolerantly<Cycle>(cycleIds.map((id) => cycleDoc(id)));

    for (const r of reviewDocs) {
      const cycle = cycleMap.get(r.cycleId);
      // A closed cycle's reviews are read-only, so anything raised here could
      // never be actioned. Fail open when the cycle document is missing rather
      // than silently hiding real work.
      if (cycle && cycle.status !== 'active') continue;
      const cycleName = cycle?.name ?? 'Unnamed cycle';
      const staffDue = fmtDate(cycle?.staffDeadline);
      const staffOverdue = isPast(cycle?.staffDeadline);

      // Past the deadline reads very differently from approaching it, so say so
      // rather than leaving the reader to compare a date against today.
      const staffDueLabel = staffDue
        ? (staffOverdue ? `Overdue — staff deadline was ${staffDue}` : `Staff deadline: ${staffDue}`)
        : null;

      if (r.status === 'notStarted') {
        items.push({ id: `self-${r.id}`, type: 'self-review', title: 'Complete your self-review', subtitle: cycleName, href: `/reviews/${r.id}`, priority: staffOverdue ? 'high' : 'medium', dueDate: staffDueLabel });
      } else if (r.status === 'inProgress') {
        items.push({ id: `self-${r.id}`, type: 'self-review', title: 'Finish and submit your self-review', subtitle: cycleName, href: `/reviews/${r.id}`, priority: staffOverdue ? 'high' : 'medium', dueDate: staffDueLabel });
      } else if (r.status === 'returnedByLineManager') {
        items.push({ id: `returned-${r.id}`, type: 'returned-review', title: 'Review returned — please amend and resubmit', subtitle: cycleName, href: `/reviews/${r.id}`, priority: 'high', dueDate: staffDueLabel });
      } else if (r.status === 'midYearOpen' && !r.midYearReview?.staffSubmittedAt) {
        items.push({ id: `midyear-staff-${r.id}`, type: 'mid-year-staff', title: 'Submit your mid-year progress comments', subtitle: cycleName, href: `/reviews/${r.id}`, priority: 'medium', dueDate: null });
      }
    }
    return items;
  }]);

  // ── Reviews I am the line manager for ───────────────────────────────────────
  // Keyed on reviewerId, not role: a school admin can be someone's line manager
  // (MANAGER_ROLES on the server includes schoolAdmin), and before this their
  // team's reviews were confirmable but never asked for. An admin may read any
  // review at their school, so the same query works for them.
  if (role === 'lineManager' || role === 'headteacher' || role === 'schoolAdmin') sections.push(['team reviews', async () => {
    const items: TodoItem[] = [];
    // Only the statuses that ask something of them. A line manager can't read a
    // review before it's submitted, so an unconstrained query is denied.
    const teamReviewsSnap = await getDocs(
      query(reviewsCol, where('schoolId', '==', schoolId), where('reviewerId', '==', uid), where('status', 'in', ['submittedByStaff', 'midYearOpen'])),
    );
    const teamDocs = teamReviewsSnap.docs.map((d) => ({ ...d.data(), id: d.id } as Review & { id: string }));
    const teamCycleIds = [...new Set(teamDocs.map((r) => r.cycleId))];
    const teamCycleMap = await readDocsTolerantly<Cycle>(teamCycleIds.map((id) => cycleDoc(id)));

    const revieweeIds = [...new Set(teamDocs.map((r) => r.revieweeId).filter(Boolean))];
    const userMap = await readDocsTolerantly<AppUser>(revieweeIds.map((id) => userDoc(id)));

    for (const r of teamDocs) {
      if (r.revieweeId === uid) continue; // own review handled above
      const cycle = teamCycleMap.get(r.cycleId);
      // A closed cycle's reviews are read-only, so anything raised here could
      // never be actioned. Fail open when the cycle document is missing rather
      // than silently hiding real work.
      if (cycle && cycle.status !== 'active') continue;
      const cycleName = cycle?.name ?? 'Unnamed cycle';
      const revieweeName = userMap.get(r.revieweeId)?.displayName ?? 'Staff member';
      const lmDue = fmtDate(cycle?.lineManagerDeadline);
      const lmOverdue = isPast(cycle?.lineManagerDeadline);

      if (r.status === 'submittedByStaff') {
        const lmDueLabel = lmDue
          ? (lmOverdue ? `Overdue — line manager deadline was ${lmDue}` : `LM deadline: ${lmDue}`)
          : null;
        items.push({ id: `confirm-${r.id}`, type: 'confirm-review', title: `Confirm ${revieweeName}'s annual review`, subtitle: cycleName, href: `/reviews/${r.id}`, priority: lmOverdue ? 'high' : 'medium', dueDate: lmDueLabel });
      } else if (r.status === 'midYearOpen' && r.midYearReview?.staffSubmittedAt) {
        items.push({ id: `midyear-lm-${r.id}`, type: 'mid-year-lm', title: `Complete mid-year check-in for ${revieweeName}`, subtitle: cycleName, href: `/reviews/${r.id}`, priority: 'medium', dueDate: null });
      }
    }
    return items;
  }]);

  // ── School admin cycle tasks ────────────────────────────────────────────────
  if (role === 'schoolAdmin') sections.push(['cycle administration', async () => {
    const items: TodoItem[] = [];
    const cyclesSnap = await getDocs(query(cyclesCol, where('schoolId', '==', schoolId)));
    const today = new Date(); today.setHours(0, 0, 0, 0);

    for (const cd of cyclesSnap.docs) {
      const c = { ...cd.data(), id: cd.id } as Cycle & { id: string };

      if (c.status === 'draft') {
        items.push({ id: `launch-${c.id}`, type: 'launch-cycle', title: `Launch cycle: ${c.name}`, subtitle: 'Cycle is drafted but not yet launched', href: `/cycles`, priority: 'low', dueDate: null });
      }
      if (c.status === 'active' && c.midYearDate && !c.midYearTriggeredAt) {
        const midYearDate = (c.midYearDate as unknown as { toDate: () => Date }).toDate();
        if (midYearDate <= today) {
          items.push({ id: `midyear-open-${c.id}`, type: 'open-mid-year', title: `Open mid-year reviews for ${c.name}`, subtitle: `Mid-year date was ${fmtDate(c.midYearDate)}`, href: `/cycles`, priority: 'high', dueDate: null });
        }
      }
      if (c.status === 'active') {
        const cycleReviewsSnap = await getDocs(query(reviewsCol, where('schoolId', '==', schoolId), where('cycleId', '==', c.id)));
        const cycleReviews = cycleReviewsSnap.docs.map((d) => d.data() as Review);
        const outstanding = cycleReviews.filter((r) => r.status === 'notStarted' || r.status === 'inProgress').length;
        const awaitingLM = cycleReviews.filter((r) => r.status === 'submittedByStaff').length;
        const returned = cycleReviews.filter((r) => r.status === 'returnedByLineManager').length;
        const lmDeadlineOverdue = isPast(c.lineManagerDeadline);

        if (awaitingLM > 0) {
          items.push({ id: `awaiting-lm-${c.id}`, type: 'admin-awaiting-lm', title: `${awaitingLM} review${awaitingLM !== 1 ? 's' : ''} awaiting line manager action`, subtitle: c.name, href: `/cycles/${c.id}/overview`, priority: lmDeadlineOverdue ? 'high' : 'medium', dueDate: fmtDate(c.lineManagerDeadline) ? `LM deadline: ${fmtDate(c.lineManagerDeadline)}` : null });
        }
        if (returned > 0) {
          items.push({ id: `returned-admin-${c.id}`, type: 'admin-returned', title: `${returned} review${returned !== 1 ? 's' : ''} returned — awaiting staff resubmission`, subtitle: c.name, href: `/cycles/${c.id}/overview`, priority: 'medium', dueDate: null });
        }
        if (outstanding > 0 && isPast(c.staffDeadline)) {
          items.push({ id: `overdue-${c.id}`, type: 'admin-overdue', title: `${outstanding} staff overdue for self-review submission`, subtitle: c.name, href: `/cycles/${c.id}/overview`, priority: 'high', dueDate: `Staff deadline was ${fmtDate(c.staffDeadline)}` });
        }
      }
    }
    return items;
  }]);

  // ── Staff with no line manager ────────────────────────────────────────
  // Deliberately one item however many people it covers: an admin wants to
  // know there is a gap and go fix it in one pass, not read twelve near
  // identical rows. SSO self-provisioning makes this continuous rather than
  // a setup step — everyone who signs in arrives as `staff` with nobody
  // above them, so new joiners keep appearing here all year.
  //
  // A user document only exists once someone has signed in (or an admin has
  // provisioned them), so the `users` read alone misses every current MIS
  // staff member who has not yet arrived. Those come from the Wonde staff
  // directory: a current record with an email (what the roster page calls
  // "MIS staff with no account") that no user document is linked to. They
  // have no line manager by definition, and the same page provisions them.
  //
  // A line manager the admin has already drawn up, waiting for a sign-in to
  // apply it, is not a gap: the admin has done their part. Those sit in
  // pendingLineManagers keyed by account or by MIS record, so both are
  // checked (an SSO account from before the link was recorded is only
  // reachable from its MIS record by email).
  //
  // Worked out on the server (getLineManagerGaps): doing it here meant
  // downloading every staff directory record — about a megabyte at a large
  // secondary — on every Home load, and that was the read that stalled.
  if (role === 'schoolAdmin') sections.push(['line managers', async () => {
    const { signedIn, notSignedIn: m, names, hasLiveCycle } = (await getLineManagerGaps()).data;
    const n = signedIn + m;
    if (n === 0) return [];
    const notSignedIn = m > 0 ? `${m} ${m === 1 ? 'has' : 'have'} not signed in yet` : null;
    const who = n <= 3 ? names.join(', ') : `${names.slice(0, 2).join(', ')} and ${n - 2} others`;
    return [{
      id: 'assign-line-managers',
      type: 'assign-line-manager',
      title: `Assign a line manager for ${n} member${n !== 1 ? 's' : ''} of staff`,
      subtitle: notSignedIn ? `${who} — ${notSignedIn}` : who,
      // Only the not-signed-in half has its own list to jump to.
      href: m > 0 && signedIn === 0 ? '/school/staff#not-signed-in' : '/school/staff',
      priority: hasLiveCycle ? 'high' : 'medium',
      dueDate: hasLiveCycle ? 'Needed before their reviews can be assigned' : null,
    }];
  }]);

  const results = await Promise.all(sections.map(([name, load]) =>
    withDeadline(load(), SECTION_DEADLINE_MS).then(
      (items) => ({ items, ok: true }),
      (err) => {
        console.error(`[todos] ${name} did not load:`, err);
        return { items: [] as TodoItem[], ok: false };
      },
    )));
  // Stable sort: within a priority, sections keep the order they are listed in.
  const items = results.flatMap((r) => r.items)
    .sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);
  return { items, incomplete: results.some((r) => !r.ok) };
}

/** Rejects if `p` has not settled in time. The read carries on underneath —
 *  Firestore has no way to cancel one — but nothing waits for it. */
function withDeadline<T>(p: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`No answer after ${ms / 1000}s.`)), ms);
  });
  return Promise.race([p, deadline]).finally(() => clearTimeout(timer));
}
